import { supabase } from "@/integrations/supabase/client";
import { withOwner } from "@/lib/supabase/withOwner";
import { ensureCanonicalSubstanceFromInput } from "@/lib/pharmacology/smartSubstance";
import { canonicalText, substanceTokens } from "@/lib/pharmacology/substanceCatalog";
import {
  createPatientMedicationFromCanonical,
  createPsyLogEvent,
  ensureCanonicalSubstance,
  ensureFormulation,
  upsertPatientByName,
} from "@/lib/clinicalPersistence";
import {
  canonicalPsyLogSubstance,
  normalizeNativePsyLog,
  type NativePsyLogExport,
  type NormalizedPsyLogIngestion,
} from "@/lib/clinical/psylogNative";

const RICK = "Henrique Ruicherlei de Santana Fernandes";

const PRESCRIBED_DEFAULTS: Record<string, {
  displayName: string;
  dose: number;
  doseUnit: string;
  frequency: string;
  usualTime: string;
  formulationName: string;
  curveModel: string;
  rationale: string;
}> = {
  Lisdexanfetamina: {
    displayName: "Lyberdia",
    dose: 70,
    doseUnit: "mg",
    frequency: "1 comprimido pela manhã",
    usualTime: "10:00",
    formulationName: "Cápsula oral pró-fármaco",
    curveModel: "prodrug_with_bioactivation",
    rationale: "TDAH grave e função executiva; monitorar redose, sono, cafeína e risco autonômico.",
  },
  Clonidina: {
    displayName: "Clonidina",
    dose: 0.3,
    doseUnit: "mg",
    frequency: "2 comprimidos de 0,150 mg à noite; SOS registrado separadamente",
    usualTime: "00:20",
    formulationName: "Comprimido oral de liberação imediata",
    curveModel: "immediate_release_antiadrenergic",
    rationale: "Hiperalerta, pesadelos, sono e descarga noradrenérgica; monitorar PA/FC e dose total.",
  },
  Clonazepam: {
    displayName: "Clonazepam",
    dose: 2,
    doseUnit: "mg",
    frequency: "1 comprimido ao deitar",
    usualTime: "00:20",
    formulationName: "Comprimido oral",
    curveModel: "sedative_long_half_life",
    rationale: "Sono/ansiedade noturna; monitorar tolerância, sedação residual e associação com depressores do SNC.",
  },
};

export type NativeImportMode = "preservar" | "atualizar_contexto";

export type NativeImportCounters = {
  patientsCreated: number;
  sessionsCreated: number;
  sessionsPreserved: number;
  dosesCreated: number;
  dosesUpdated: number;
  dosesPreserved: number;
  substanceUseCreated: number;
  substanceUsePreserved: number;
  summariesCreated: number;
  summariesPreserved: number;
  substancesCreated: number;
  formulationsCreated: number;
  medicationsCreated: number;
  warnings: string[];
};

function counters(): NativeImportCounters {
  return {
    patientsCreated: 0,
    sessionsCreated: 0,
    sessionsPreserved: 0,
    dosesCreated: 0,
    dosesUpdated: 0,
    dosesPreserved: 0,
    substanceUseCreated: 0,
    substanceUsePreserved: 0,
    summariesCreated: 0,
    summariesPreserved: 0,
    substancesCreated: 0,
    formulationsCreated: 0,
    medicationsCreated: 0,
    warnings: [],
  };
}

function key(patientId: string, name: string, at: string) {
  return `${patientId}|${canonicalText(name)}|${new Date(at).toISOString()}`;
}

function sessionName(localDate: string) {
  return `PsyLog — ${localDate.split("-").reverse().join("/")}`;
}

function sourceNote(row: NormalizedPsyLogIngestion) {
  return [
    `Origem: exportação PsyLog; experiência: ${row.sourceExperienceTitle}.`,
    `Registro-fonte criado em ${row.creationDate ? new Date(row.creationDate).toISOString() : "horário não disponível"}.`,
    row.consumerName ? `consumerName: ${row.consumerName}.` : "consumerName vazio: atribuído ao paciente padrão selecionado.",
    row.isDoseAnEstimate ? "Dose marcada como estimativa no arquivo-fonte." : "Dose registrada como não estimada no arquivo-fonte.",
    row.stomachFullness ? `Contexto alimentar original: ${row.stomachFullness} (${row.stomachContext.label}).` : null,
    row.warnings.length ? `Revisar: ${row.warnings.join(" ")}` : null,
  ].filter(Boolean).join(" ");
}

function daySummary(rows: NormalizedPsyLogIngestion[]) {
  const bySubstance = rows.reduce<Record<string, number>>((acc, row) => {
    acc[row.displayName] = (acc[row.displayName] ?? 0) + 1;
    return acc;
  }, {});
  const redoses = rows.filter((row) => row.logType === "redose" || row.logType === "sobreposicao").length;
  const nonPrescribed = rows.filter((row) => row.kind === "opioide" || row.kind === "alcool").length;
  return {
    redoses,
    nonPrescribed,
    text: Object.entries(bySubstance).map(([name, count]) => `${name}: ${count}`).join("; "),
  };
}

async function ensureStructure(user: any, cache: any[], row: NormalizedPsyLogIngestion, result: NativeImportCounters) {
  const mapped = canonicalPsyLogSubstance(row.substanceName);
  const tokens = [mapped.input, mapped.display, mapped.brand, row.substanceName].filter(Boolean).map(canonicalText);
  const existedBefore = cache.some((candidate) => substanceTokens(candidate).some((token) => tokens.includes(canonicalText(token))));
  let substance: any;
  try {
    const ensured = await ensureCanonicalSubstanceFromInput(mapped.input, user, cache);
    substance = ensured.substance;
    if (ensured.created && !existedBefore) result.substancesCreated++;
    result.formulationsCreated += ensured.formulationsCreated ?? 0;
  } catch {
    substance = await ensureCanonicalSubstance(user, {
      name: mapped.display,
      aliases: [row.substanceName, mapped.brand].filter(Boolean) as string[],
      default_dose_unit: row.units ?? "mg",
    });
    if (!existedBefore) result.substancesCreated++;
  }
  if (!cache.some((candidate) => candidate.id === substance.id)) cache.push(substance);

  const defaults = PRESCRIBED_DEFAULTS[row.displayName];
  const formulationName = defaults?.formulationName ?? `${row.routeNormalized === "oral" ? "Formulação oral" : row.routeNormalized} — registro PsyLog`;
  const formRead = await (supabase as any).from("substance_formulations").select("*").eq("substance_id", substance.id).order("is_default", { ascending: false });
  if (formRead.error) throw formRead.error;
  let formulation = (formRead.data ?? []).find((candidate: any) => canonicalText(candidate.formulation_name) === canonicalText(formulationName)) ?? null;
  if (!formulation) {
    formulation = await ensureFormulation(user, {
      substance_id: substance.id,
      formulation_name: formulationName,
      route: row.routeNormalized,
      curve_model: defaults?.curveModel ?? "custom_relative",
      is_default: true,
    });
    result.formulationsCreated++;
  }
  return { substance, formulation };
}

async function ensureMedication(
  user: any,
  patient: any,
  defaultPatient: string,
  substance: any,
  formulation: any,
  row: NormalizedPsyLogIngestion,
  medications: any[],
  result: NativeImportCounters,
) {
  const defaults = PRESCRIBED_DEFAULTS[row.displayName];
  if (!defaults) return null;
  const existing = medications.find((medication) => medication.substance_id === substance.id)
    ?? medications.find((medication) => canonicalText(medication.brand_name) === canonicalText(defaults.displayName));
  if (existing) return existing;
  if (canonicalText(patient.full_name) !== canonicalText(defaultPatient)) {
    result.warnings.push(`${row.localDate} ${row.localTime} — ${row.displayName} atribuído a ${patient.full_name}: ingestão preservada sem criar tratamento ativo.`);
    return null;
  }
  const created = await createPatientMedicationFromCanonical(user, {
    patient_id: patient.id,
    substance_id: substance.id,
    formulation_id: formulation?.id ?? null,
    display_name: defaults.displayName,
    canonical_name: substance.name,
    current_dose: defaults.dose,
    dose_unit: defaults.doseUnit,
    frequency: defaults.frequency,
    usual_time: defaults.usualTime,
    status: "ativo",
    clinical_rationale: defaults.rationale,
    monitoring_plan: "Registrar horário real, alimento, sono, cafeína, resposta, adversos, redose e eventos de segurança.",
    patient_notes: "Tratamento criado pelo importador PsyLog somente quando o paciente é o padrão/Rick; revisar sem apagar registros manuais.",
  });
  medications.push(created);
  result.medicationsCreated++;
  return created;
}

export async function importNativePsyLog(input: {
  user: any;
  data: NativePsyLogExport;
  defaultPatient: string;
  alicePatient: string;
  sourceFile: string;
  mode: NativeImportMode;
}) {
  if (!input.user?.id) throw new Error("Sessão autenticada obrigatória.");
  const result = counters();
  const rows = normalizeNativePsyLog(input.data, input.defaultPatient, input.alicePatient);
  const groups = new Map<string, NormalizedPsyLogIngestion[]>();
  for (const row of rows) groups.set(`${canonicalText(row.patientName)}|${row.localDate}`, [...(groups.get(`${canonicalText(row.patientName)}|${row.localDate}`) ?? []), row]);
  const dayGroups = [...groups.values()].map((group) => group.sort((a, b) => a.time - b.time));
  const firstAt = rows[0]?.atIso;
  const lastAt = rows.at(-1)?.atIso;
  if (!firstAt || !lastAt) throw new Error("Exportação sem ingestões datadas.");

  const substanceRead = await (supabase as any).from("substances").select("*");
  if (substanceRead.error) throw substanceRead.error;
  const substanceCache = [...(substanceRead.data ?? [])];
  const patientCache = new Map<string, any>();
  const medicationsByPatient = new Map<string, any[]>();
  const doseCache = new Map<string, any>();
  const useCache = new Map<string, any>();
  const sessionCache = new Map<string, any>();

  for (const patientName of [...new Set(rows.map((row) => row.patientName))]) {
    const before = await (supabase as any).from("patients").select("id").eq("owner_id", input.user.id).ilike("full_name", patientName).maybeSingle();
    if (before.error) throw before.error;
    const patient = await upsertPatientByName(input.user, patientName, {
      social_name: canonicalText(patientName) === canonicalText(input.defaultPatient) ? "Rick" : undefined,
      birth_date: canonicalText(patientName) === canonicalText(input.defaultPatient) ? "2005-04-30" : undefined,
      status: "ativo",
      tags: ["PsyLog", "importação retrospectiva"],
    });
    if (!before.data) result.patientsCreated++;
    patientCache.set(patientName, patient);

    const medicationRead = await (supabase as any).from("patient_medications").select("*").eq("owner_id", input.user.id).eq("patient_id", patient.id);
    if (medicationRead.error) throw medicationRead.error;
    medicationsByPatient.set(patient.id, medicationRead.data ?? []);

    const doseRead = await (supabase as any).from("medication_dose_logs").select("*").eq("owner_id", input.user.id).eq("patient_id", patient.id).gte("actual_time", firstAt).lte("actual_time", lastAt);
    if (doseRead.error) throw doseRead.error;
    for (const dose of doseRead.data ?? []) doseCache.set(key(patient.id, dose.substance_name, dose.actual_time), dose);

    const useRead = await (supabase as any).from("substance_use_logs").select("*").eq("owner_id", input.user.id).eq("patient_id", patient.id).gte("used_at", firstAt).lte("used_at", lastAt);
    if (useRead.error) throw useRead.error;
    for (const use of useRead.data ?? []) useCache.set(key(patient.id, use.substance_name, use.used_at), use);

    const sessionRead = await (supabase as any).from("clinical_sessions").select("*").eq("owner_id", input.user.id).eq("patient_id", patient.id).gte("session_at", firstAt).lte("session_at", lastAt);
    if (sessionRead.error) throw sessionRead.error;
    for (const session of sessionRead.data ?? []) sessionCache.set(`${patient.id}|${session.name}`, session);
  }

  const structureCache = new Map<string, { substance: any; formulation: any }>();
  for (const group of dayGroups) {
    const patient = patientCache.get(group[0].patientName);
    if (!patient) throw new Error(`Paciente não resolvido: ${group[0].patientName}`);
    const name = sessionName(group[0].localDate);
    const summary = daySummary(group);
    let session = sessionCache.get(`${patient.id}|${name}`);
    if (!session) {
      const sessionInsert = await (supabase as any).from("clinical_sessions").insert(withOwner({
        patient_id: patient.id,
        name,
        session_type: "monitoramento PsyLog",
        session_status: "realizada",
        status: "realizada",
        session_at: group[0].atIso,
        scheduled_start_at: group[0].atIso,
        is_retroactive: true,
        complaint: "Registro retrospectivo de doses e substâncias importado do PsyLog.",
        therapeutic_goal: "Reconstruir curva temporal, contexto alimentar, redose, sobreposição e risco clínico.",
        clinical_summary: `${group.length} ingestão(ões). ${summary.text}. Redose/sobreposição: ${summary.redoses}. Uso não prescrito: ${summary.nonPrescribed}.`,
        patient_narrative: [...new Set(group.map((row) => row.sourceExperienceText).filter(Boolean))].join("\n\n") || null,
        notes: `Importação idempotente; timestamps preservados em America/Sao_Paulo. Fonte: ${input.sourceFile || "PsyLog JSON"}.`,
      }, input.user)).select("*").single();
      if (sessionInsert.error) throw sessionInsert.error;
      session = sessionInsert.data;
      sessionCache.set(`${patient.id}|${name}`, session);
      result.sessionsCreated++;
    } else result.sessionsPreserved++;

    const existingSummary = await (supabase as any).from("patient_psylog_events").select("id").eq("owner_id", input.user.id).eq("patient_id", patient.id).eq("event_at", group[0].atIso).eq("event_type", "resumo_diario_importado").eq("title", name).maybeSingle();
    if (existingSummary.error) throw existingSummary.error;
    await createPsyLogEvent(input.user, {
      patient_id: patient.id,
      event_at: group[0].atIso,
      event_type: "resumo_diario_importado",
      title: name,
      description: `${summary.text}. ${group.some((row) => row.warnings.length) ? `Revisar: ${[...new Set(group.flatMap((row) => row.warnings))].join(" ")}` : "Sem anomalia estrutural adicional no arquivo-fonte."}`,
      linked_session_id: session.id,
      metadata: {
        local_date: group[0].localDate,
        first_at: group[0].atIso,
        last_at: group.at(-1)!.atIso,
        ingestion_count: group.length,
        redose_or_overlap_count: summary.redoses,
        non_prescribed_count: summary.nonPrescribed,
        warnings: [...new Set(group.flatMap((row) => row.warnings))],
        source_file: input.sourceFile || "PsyLog JSON",
      },
    });
    if (existingSummary.data) result.summariesPreserved++;
    else result.summariesCreated++;

    for (const row of group) {
      let structure = structureCache.get(row.displayName);
      if (!structure) {
        structure = await ensureStructure(input.user, substanceCache, row, result);
        structureCache.set(row.displayName, structure);
      }
      const medications = medicationsByPatient.get(patient.id) ?? [];
      const patientMedication = await ensureMedication(input.user, patient, input.defaultPatient, structure.substance, structure.formulation, row, medications, result);
      medicationsByPatient.set(patient.id, medications);

      const doseId = key(patient.id, row.displayName, row.atIso);
      const existingDose = doseCache.get(doseId);
      const payload = {
        patient_id: patient.id,
        session_id: session.id,
        patient_medication_id: patientMedication?.id ?? null,
        substance_id: structure.substance.id,
        formulation_id: structure.formulation?.id ?? null,
        substance_name: row.displayName,
        formulation_name: structure.formulation?.formulation_name ?? null,
        formulation: structure.formulation?.formulation_type ?? null,
        dose_amount: row.dose ?? null,
        dose_unit: row.units ?? null,
        dose_text: row.dose != null ? `${row.isDoseAnEstimate ? "≈" : ""}${row.dose} ${row.units ?? ""}`.trim() : null,
        actual_time: row.atIso,
        route: row.routeNormalized,
        log_type: row.logType,
        dose_goal: row.logType,
        clinical_reason: row.kind === "opioide"
          ? "uso não prescrito / redução de danos"
          : row.kind === "interferente"
            ? "interferente farmacológico"
            : row.kind === "prn"
              ? "uso SOS/PRN registrado"
              : patientMedication
                ? "registro de dose real vinculado ao tratamento"
                : "ingestão registrada; vínculo terapêutico não presumido",
        stomach: row.stomachContext.label,
        stomach_fullness_0_10: row.stomachContext.fullness0to10,
        meal_size: null,
        food_context: row.stomachContext.foodContext,
        taken_with_food: row.stomachContext.takenWithFood,
        caffeine_amount: row.displayName === "Cafeína" ? row.dose ?? null : null,
        caffeine_near_dose_mg: row.nearbyCaffeineMg,
        notes_patient: row.notes || null,
        notes_clinician: sourceNote(row),
      };

      if (existingDose && input.mode === "preservar") result.dosesPreserved++;
      else if (existingDose) {
        const update = await (supabase as any).from("medication_dose_logs").update(payload).eq("id", existingDose.id).eq("owner_id", input.user.id);
        if (update.error) throw update.error;
        result.dosesUpdated++;
      } else {
        const insert = await (supabase as any).from("medication_dose_logs").insert(withOwner(payload, input.user)).select("*").single();
        if (insert.error) throw insert.error;
        doseCache.set(doseId, insert.data);
        result.dosesCreated++;
      }

      if (row.kind === "opioide" || row.kind === "alcool") {
        const useId = key(patient.id, row.displayName, row.atIso);
        if (useCache.has(useId)) result.substanceUsePreserved++;
        else {
          const useInsert = await (supabase as any).from("substance_use_logs").insert(withOwner({
            patient_id: patient.id,
            session_id: session.id,
            substance_id: structure.substance.id,
            substance_name: row.displayName,
            used_at: row.atIso,
            amount: row.dose ?? null,
            unit: row.units ?? null,
            route: row.routeNormalized,
            intent: row.kind === "opioide" ? "recaída/uso não prescrito" : "uso registrado",
            consequence: row.kind === "opioide" ? "risco físico" : "sem dano documentado",
            context: [row.stomachContext.foodContext, `Importado do PsyLog (${row.sourceExperienceTitle}).`].filter(Boolean).join(" "),
            physician_notes: row.warnings.join(" ") || "Dose/horário preservados do arquivo-fonte; craving e consequência clínica não estavam informados.",
          }, input.user));
          if (useInsert.error) throw useInsert.error;
          useCache.set(useId, true);
          result.substanceUseCreated++;
        }
      }
      result.warnings.push(...row.warnings.map((warning) => `${row.localDate} ${row.localTime} — ${row.displayName}: ${warning}`));
    }
  }

  result.warnings = [...new Set(result.warnings)];
  return result;
}

export const DEFAULT_PSYLOG_PATIENT = RICK;
